/*
 * Optional-preference ranking for the Net Return self-serve setup catalog.
 *
 * This module never filters the catalog and never decides compatibility. The
 * strict configurator foundation remains the authority for eligibility; pass
 * its evaluateCatalog() output as options.decisions when selection controls
 * need to be enabled or disabled.
 */
(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.NRSelfServeRanking = api;
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var SECTION_DEFINITIONS = [
    { id: 'nets', label: 'Nets' },
    { id: 'packages', label: 'Packages' },
    { id: 'simulator_bays', label: 'Simulator Bays' },
    { id: 'launch_monitors', label: 'Technology' },
    { id: 'protection', label: 'Protection' },
    { id: 'hitting_surfaces', label: 'Hitting Surfaces' },
    { id: 'simulation', label: 'Simulation' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'catalog', label: 'More Products' }
  ];

  var ROLE_SECTIONS = {
    base: 'nets',
    package: 'packages',
    simulator_bay: 'simulator_bays',
    protection: 'protection',
    hitting_surface: 'hitting_surfaces',
    launch_monitor: 'launch_monitors',
    simulation: 'simulation',
    finishing: 'accessories',
    replacement: 'maintenance'
  };

  var FAMILY_SECTIONS = {
    base: 'nets',
    base_package: 'packages',
    package: 'packages',
    simulator_bay: 'simulator_bays',
    protection: 'protection',
    hitting_surface: 'hitting_surfaces',
    launch_monitor: 'launch_monitors',
    simulation: 'simulation',
    essentials: 'accessories',
    training: 'accessories',
    finishing: 'accessories',
    mixed: 'accessories',
    replacement: 'maintenance'
  };

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function finitePositiveNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    var number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function normalizeSimulation(value) {
    if (value === true) return 'yes';
    if (value === false) return 'no';
    if (typeof value !== 'string') return null;
    var normalized = value.trim().toLowerCase();
    if (['yes', 'true', 'simulation', 'simulator'].indexOf(normalized) !== -1) return 'yes';
    if (['no', 'false'].indexOf(normalized) !== -1) return 'no';
    return null;
  }

  function normalizeEnvironment(value) {
    if (typeof value !== 'string') return null;
    var normalized = value.trim().toLowerCase();
    return normalized === 'indoor' || normalized === 'outdoor' ? normalized : null;
  }

  function normalizeAnswers(input) {
    input = isObject(input) ? input : {};
    var rawSpace = isObject(input.space) ? input.space : {};
    var space = {
      widthFt: finitePositiveNumber(rawSpace.widthFt !== undefined ? rawSpace.widthFt : rawSpace.width),
      depthFt: finitePositiveNumber(rawSpace.depthFt !== undefined ? rawSpace.depthFt : rawSpace.depth),
      heightFt: finitePositiveNumber(rawSpace.heightFt !== undefined ? rawSpace.heightFt : rawSpace.height)
    };

    return {
      space: space,
      simulation: normalizeSimulation(input.simulation),
      environment: normalizeEnvironment(input.environment)
    };
  }

  function hasPreferences(answers) {
    return Boolean(
      answers.simulation ||
      answers.environment ||
      answers.space.widthFt !== null ||
      answers.space.depthFt !== null ||
      answers.space.heightFt !== null
    );
  }

  function normalizedText(value) {
    return typeof value === 'string'
      ? value.toLowerCase().replace(/[_-]+/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim()
      : '';
  }

  function productText(product) {
    var parts = [product && product.handle, product && product.title, product && product.role, product && product.step];
    var sources = product && Array.isArray(product.sources) ? product.sources : [];
    var tags = product && Array.isArray(product.recommendationTags) ? product.recommendationTags : [];

    sources.forEach(function (source) {
      parts.push(source && source.collectionHandle, source && source.family);
    });
    parts = parts.concat(tags);
    return normalizedText(parts.filter(Boolean).join(' '));
  }

  function hasPhrase(text, phrase) {
    var candidate = normalizedText(phrase);
    return candidate !== '' && (' ' + text + ' ').indexOf(' ' + candidate + ' ') !== -1;
  }

  function hasAnyPhrase(text, phrases) {
    return phrases.some(function (phrase) { return hasPhrase(text, phrase); });
  }

  function recommendationTags(product) {
    return product && Array.isArray(product.recommendationTags) ? product.recommendationTags : [];
  }

  function hasTag(product, tag) {
    return recommendationTags(product).indexOf(tag) !== -1;
  }

  function productEnvironments(product) {
    return product && Array.isArray(product.environment)
      ? product.environment.map(normalizedText).filter(Boolean)
      : [];
  }

  function minimumDimension(product, dimension) {
    var space = product && isObject(product.minimumSpaceFt) ? product.minimumSpaceFt : {};
    var value = space[dimension];
    if (value === undefined) value = space[dimension + 'Ft'];
    return finitePositiveNumber(value);
  }

  function groupCandidate(group, score, key, label) {
    return { group: group, score: score, key: key || null, label: label || null };
  }

  function betterCandidate(current, candidate) {
    if (!current || candidate.score > current.score) return candidate;
    return current;
  }

  function spaceCandidate(product, answers) {
    var dimensions = [
      { key: 'width', answer: answers.space.widthFt },
      { key: 'depth', answer: answers.space.depthFt },
      { key: 'height', answer: answers.space.heightFt }
    ].filter(function (item) { return item.answer !== null; });

    if (!dimensions.length) return null;

    var known = 0;
    var mismatch = false;
    dimensions.forEach(function (item) {
      var minimum = minimumDimension(product, item.key);
      if (minimum === null) return;
      known += 1;
      if (item.answer < minimum) mismatch = true;
    });

    if (mismatch) return groupCandidate('space', -70, null, null);
    if (known !== dimensions.length) return groupCandidate('space', 0, null, null);
    return groupCandidate('space', Math.min(36, dimensions.length * 12), 'space-match', 'Space match');
  }

  function environmentCandidate(product, answers, text) {
    var environment = answers.environment;
    if (!environment) return null;

    var environments = productEnvironments(product);
    var handle = product && typeof product.handle === 'string' ? product.handle : '';
    var hasEnvironmentData = environments.length > 0;
    var matchesEnvironment = environments.indexOf(environment) !== -1;
    var candidate = null;

    if (hasEnvironmentData && !matchesEnvironment) {
      return groupCandidate('environment', -55, null, null);
    }

    if (matchesEnvironment) {
      candidate = groupCandidate(
        'environment',
        36,
        environment + '-use',
        environment === 'indoor' ? 'Indoor use' : 'Outdoor use'
      );
    }

    if (environment === 'outdoor') {
      if (hasAnyPhrase(text, ['outdoor cover'])) {
        candidate = betterCandidate(candidate, groupCandidate('environment', 95, 'outdoor-cover', 'Outdoor cover'));
      } else if (hasAnyPhrase(text, ['net guardian'])) {
        candidate = betterCandidate(candidate, groupCandidate('environment', 82, 'net-protection', 'Net protection'));
      } else if (hasAnyPhrase(text, ['sandbag', 'sandbags'])) {
        candidate = betterCandidate(candidate, groupCandidate('environment', 46, 'added-stability', 'Added stability'));
      }
    }

    if (environment === 'indoor') {
      if (handle === 'no-fly-zone-v2-kits' || hasAnyPhrase(text, ['no fly zone v2 kit', 'no fly zone v2 kits', 'no fly zone kit', 'no fly zone kits'])) {
        candidate = betterCandidate(candidate, groupCandidate('environment', 95, 'overhead-coverage', 'Overhead coverage'));
      } else if (handle === 'universal-side-barriers') {
        candidate = betterCandidate(candidate, groupCandidate('environment', 92, 'side-coverage', 'Side coverage'));
      } else if (handle === 'no-fly-zone-v2') {
        candidate = betterCandidate(candidate, groupCandidate('environment', 90, 'overhead-coverage', 'Overhead coverage'));
      } else if (hasAnyPhrase(text, ['side barrier', 'side barriers'])) {
        candidate = betterCandidate(candidate, groupCandidate('environment', 88, 'side-coverage', 'Side coverage'));
      } else if (hasAnyPhrase(text, ['no fly zone'])) {
        candidate = betterCandidate(candidate, groupCandidate('environment', 82, 'overhead-coverage', 'Overhead coverage'));
      } else if (product && product.role === 'protection') {
        candidate = betterCandidate(candidate, groupCandidate('environment', 30, 'indoor-coverage', 'Indoor coverage'));
      } else if (hasTag(product, 'miss_protection') || hasTag(product, 'high_loft_protection')) {
        candidate = betterCandidate(candidate, groupCandidate('environment', 26, 'indoor-coverage', 'Indoor coverage'));
      }
    }

    return candidate || groupCandidate('environment', 0, null, null);
  }

  function simulationCandidate(product, answers, text) {
    if (!answers.simulation) return null;

    var role = product && product.role;
    var candidate = null;
    var simulatorNamed = hasAnyPhrase(text, ['sim kit', 'simulator kit', 'simulator bay', 'sim bay', 'impact screen', 'launch monitor']);

    if (answers.simulation === 'yes') {
      if (hasAnyPhrase(text, ['sim kit', 'simulator kit'])) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 100, 'sim-kit', 'Sim kit'));
      }
      if (role === 'launch_monitor') {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 92, 'launch-monitor', 'Launch monitor'));
      } else if (role !== 'package' && hasAnyPhrase(text, ['launch monitor'])) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 84, 'launch-monitor', 'Launch monitor'));
      }
      if (role === 'simulator_bay') {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 86, 'sim-bay', 'Sim bay'));
      } else if (role !== 'package' && hasAnyPhrase(text, ['simulator bay', 'sim bay'])) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 78, 'sim-bay', 'Sim bay'));
      }
      if (role === 'simulation') {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 72, 'simulation-gear', 'Simulation gear'));
      }
      if (hasAnyPhrase(text, ['impact screen'])) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 68, 'impact-screen', 'Impact screen'));
      }
      if (role === 'package' && simulatorNamed) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 64, 'simulator-setup', 'Simulator setup'));
      }
      if (hasTag(product, 'simulator_now')) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 58, 'simulation-match', 'Simulation match'));
      } else if (hasTag(product, 'data_training')) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 48, 'data-training', 'Data training'));
      } else if (hasTag(product, 'simulator_later')) {
        candidate = betterCandidate(candidate, groupCandidate('simulation', 38, 'sim-ready', 'Sim ready'));
      }
      return candidate || groupCandidate('simulation', 0, null, null);
    }

    if (role === 'launch_monitor' || role === 'simulation' || role === 'simulator_bay' || simulatorNamed) {
      return groupCandidate('simulation', -55, null, null);
    }
    if (hasTag(product, 'practice_only')) {
      candidate = betterCandidate(candidate, groupCandidate('simulation', 40, 'practice-focus', 'Practice focus'));
    }
    if (role === 'base') {
      candidate = betterCandidate(candidate, groupCandidate('simulation', 30, 'practice-net', 'Practice net'));
    } else if (role === 'hitting_surface') {
      candidate = betterCandidate(candidate, groupCandidate('simulation', 24, 'hitting-surface', 'Hitting surface'));
    } else if (role === 'protection') {
      candidate = betterCandidate(candidate, groupCandidate('simulation', 18, 'practice-protection', 'Practice protection'));
    } else if (role === 'package') {
      candidate = betterCandidate(candidate, groupCandidate('simulation', 16, 'practice-setup', 'Practice setup'));
    }

    return candidate || groupCandidate('simulation', 0, null, null);
  }

  function scoreProduct(product, inputAnswers) {
    var answers = normalizeAnswers(inputAnswers);
    var text = productText(product || {});
    var candidates = [
      spaceCandidate(product || {}, answers),
      environmentCandidate(product || {}, answers, text),
      simulationCandidate(product || {}, answers, text)
    ].filter(Boolean);
    var reasons = candidates.filter(function (candidate) {
      return candidate.score > 0 && candidate.key && candidate.label;
    }).sort(function (left, right) {
      return right.score - left.score;
    }).map(function (candidate) {
      return { key: candidate.key, label: candidate.label, score: candidate.score };
    });

    return {
      score: candidates.reduce(function (total, candidate) { return total + candidate.score; }, 0),
      matchedPreferenceCount: reasons.length,
      reasonTags: reasons,
      primaryReason: reasons.length ? reasons[0].label : null
    };
  }

  function sectionForProduct(product) {
    if (product && ROLE_SECTIONS[product.role]) return ROLE_SECTIONS[product.role];
    var sources = product && Array.isArray(product.sources) ? product.sources : [];
    for (var index = 0; index < sources.length; index += 1) {
      if (sources[index] && FAMILY_SECTIONS[sources[index].family]) return FAMILY_SECTIONS[sources[index].family];
    }
    return 'catalog';
  }

  function decisionsByHandle(input) {
    var decisions = {};
    if (Array.isArray(input)) {
      input.forEach(function (decision) {
        if (decision && typeof decision.handle === 'string' && decision.handle) decisions[decision.handle] = decision;
      });
      return decisions;
    }
    if (isObject(input)) {
      Object.keys(input).forEach(function (handle) {
        if (isObject(input[handle])) decisions[handle] = input[handle];
      });
    }
    return decisions;
  }

  function eligibilityFor(product, decisions) {
    var decision = product && decisions[product.handle];
    if (!decision) {
      return {
        evaluated: false,
        visible: true,
        eligible: null,
        warning: null
      };
    }
    return {
      evaluated: decision.evaluated !== false,
      visible: decision.visible !== false,
      eligible: decision.eligible === true ? true : decision.eligible === false ? false : null,
      warning: typeof decision.warning === 'string' && decision.warning ? decision.warning : null
    };
  }

  function productIsAvailable(product) {
    if (!product || product.available !== true) return false;
    if (!Array.isArray(product.variants) || product.variants.length === 0) return true;
    return product.variants.some(function (variant) { return variant && variant.available === true; });
  }

  function sourceKey(source) {
    if (!isObject(source)) return '';
    return String(source.collectionHandle || '') + '|' + String(source.family || '');
  }

  function mergeSources(current, additions) {
    var seen = {};
    var sources = [];
    (Array.isArray(current) ? current : []).concat(Array.isArray(additions) ? additions : []).forEach(function (source) {
      if (!isObject(source)) return;
      var key = sourceKey(source);
      if (seen[key]) return;
      seen[key] = true;
      sources.push(Object.assign({}, source));
    });
    return sources;
  }

  function deduplicateProducts(sourceProducts) {
    var byHandle = {};
    var entries = [];

    (Array.isArray(sourceProducts) ? sourceProducts : []).forEach(function (product, originalIndex) {
      var handle = product && typeof product.handle === 'string' ? product.handle : '';
      if (!handle || !Object.prototype.hasOwnProperty.call(byHandle, handle)) {
        var entry = {
          product: isObject(product) ? Object.assign({}, product, { sources: mergeSources([], product.sources) }) : product,
          originalIndex: originalIndex
        };
        entries.push(entry);
        if (handle) byHandle[handle] = entry;
        return;
      }

      var existing = byHandle[handle];
      existing.product.sources = mergeSources(existing.product.sources, product.sources);
    });

    return entries;
  }

  function comparisonPriority(product) {
    var priority = product && Number(product.comparisonPriority);
    return Number.isFinite(priority) && priority >= 0 ? priority : 1000;
  }

  function rankedProductComparator(withPreferences) {
    return function (left, right) {
      if (withPreferences && left.score !== right.score) return right.score - left.score;
      if (withPreferences && left.matchedPreferenceCount !== right.matchedPreferenceCount) {
        return right.matchedPreferenceCount - left.matchedPreferenceCount;
      }
      if (withPreferences && left.comparisonPriority !== right.comparisonPriority) {
        return left.comparisonPriority - right.comparisonPriority;
      }
      return left.originalIndex - right.originalIndex;
    };
  }

  function sectionDefinition(sectionId) {
    return SECTION_DEFINITIONS.find(function (section) { return section.id === sectionId; }) || SECTION_DEFINITIONS[SECTION_DEFINITIONS.length - 1];
  }

  function rankCatalog(catalog, inputAnswers, options) {
    catalog = isObject(catalog) ? catalog : {};
    options = isObject(options) ? options : {};
    var answers = normalizeAnswers(inputAnswers);
    var withPreferences = hasPreferences(answers);
    var decisions = decisionsByHandle(options.decisions);
    var sourceProducts = Array.isArray(catalog.products) ? catalog.products : [];
    var products = deduplicateProducts(sourceProducts).map(function (sourceEntry) {
      var product = sourceEntry.product;
      var originalIndex = sourceEntry.originalIndex;
      var relevance = scoreProduct(product, answers);
      var eligibility = eligibilityFor(product, decisions);
      return {
        product: product,
        handle: product && typeof product.handle === 'string' ? product.handle : '',
        title: product && typeof product.title === 'string' ? product.title : '',
        sectionId: sectionForProduct(product),
        score: relevance.score,
        matchedPreferenceCount: relevance.matchedPreferenceCount,
        reasonTags: relevance.reasonTags,
        primaryReason: relevance.primaryReason,
        eligibility: eligibility,
        browsable: true,
        browseable: true,
        selectable: Boolean(productIsAvailable(product) && (!eligibility.evaluated || eligibility.eligible !== false)),
        comparisonPriority: comparisonPriority(product),
        originalIndex: originalIndex
      };
    }).sort(rankedProductComparator(withPreferences));

    var sections = SECTION_DEFINITIONS.map(function (definition, sectionIndex) {
      var sectionProducts = products.filter(function (entry) { return entry.sectionId === definition.id; });
      return {
        id: definition.id,
        label: definition.label,
        score: sectionProducts.length ? Math.max.apply(null, sectionProducts.map(function (entry) { return entry.score; })) : 0,
        matchCount: sectionProducts.filter(function (entry) { return entry.score > 0; }).length,
        products: sectionProducts,
        originalIndex: sectionIndex
      };
    }).filter(function (section) { return section.products.length > 0; });

    if (withPreferences) {
      sections.sort(function (left, right) {
        if (left.score !== right.score) return right.score - left.score;
        if (left.matchCount !== right.matchCount) return right.matchCount - left.matchCount;
        return left.originalIndex - right.originalIndex;
      });
    }

    return {
      answers: answers,
      hasPreferences: withPreferences,
      products: products,
      sections: sections.map(function (section) {
        return {
          id: section.id,
          label: section.label,
          score: section.score,
          matchCount: section.matchCount,
          products: section.products
        };
      })
    };
  }

  return {
    normalizeAnswers: normalizeAnswers,
    scoreProduct: scoreProduct,
    sectionForProduct: sectionForProduct,
    rankCatalog: rankCatalog,
    constants: {
      sections: SECTION_DEFINITIONS.map(function (section) {
        return { id: section.id, label: section.label };
      })
    }
  };
});
